import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useUpsertDocumentTemplate } from "../../../../api/mutations/documents.mutations";
import { ENTITY_TEMPLATE_MAPPINGS, entityModels } from "@dune/vars";

interface CreateTemplateDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	upsertTemplate: ReturnType<typeof useUpsertDocumentTemplate>;
	onSuccess: () => void;
}

const CreateTemplateDialog: React.FC<CreateTemplateDialogProps> = ({
	open,
	onOpenChange,
	upsertTemplate,
	onSuccess,
}) => {
	const [createName, setCreateName] = useState("");
	const [createEntityModel, setCreateEntityModel] = useState("");
	const [createTemplateType, setCreateTemplateType] = useState("");
	const [createFile, setCreateFile] = useState<File | null>(null);

	const templateTypes = useMemo(() => {
		return ENTITY_TEMPLATE_MAPPINGS[createEntityModel];
	}, [createEntityModel]);

	// Reset template type when entity model changes
	useEffect(() => {
		if (createEntityModel && createTemplateType) {
			// Check if current template type is still valid for the new entity model
			if (templateTypes && !templateTypes.includes(createTemplateType)) {
				setCreateTemplateType("");
			}
		}
	}, [createEntityModel, templateTypes, createTemplateType]);

	const handleCreate = () => {
		if (!createName || !createEntityModel || !createTemplateType || !createFile)
			return;

		upsertTemplate.mutate(
			{
				name: createName,
				entityModel: createEntityModel,
				templateType: createTemplateType,
				file: createFile,
			},
			{
				onSuccess: () => {
					toast.success("Modèle créé");
					setCreateName("");
					setCreateEntityModel("");
					setCreateTemplateType("");
					setCreateFile(null);
					onSuccess();
				},
			},
		);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[40%] min-w-[400px]">
				<DialogHeader>
					<DialogTitle>Créer un nouveau modèle</DialogTitle>
					<DialogDescription>
						Indiquez le nom, le type et le fichier .docx
					</DialogDescription>
				</DialogHeader>
				<Input
					type="text"
					placeholder="Nom du modèle"
					value={createName}
					onChange={(e) => setCreateName(e.target.value)}
					className="mb-2"
				/>
				<Select
					value={createEntityModel}
					onValueChange={(value) => setCreateEntityModel(value)}
				>
					<SelectTrigger size="default" className="mb-2 w-full">
						<SelectValue placeholder="Sélectionner le modèle d'entité" />
					</SelectTrigger>
					<SelectContent>
						{entityModels?.map((model) => (
							<SelectItem key={model} value={model}>
								{model}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{/* Sélecteur de type de template */}
				<Select
					value={createTemplateType}
					onValueChange={(value) => setCreateTemplateType(value)}
				>
					<SelectTrigger size="default" className="mb-2 w-full">
						<SelectValue placeholder="Sélectionner le type de template" />
					</SelectTrigger>
					<SelectContent>
						{templateTypes?.map((type) => (
							<SelectItem key={type} value={type}>
								{type}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				<Input
					type="file"
					accept=".docx"
					onChange={(e) =>
						e.target.files?.[0] && setCreateFile(e.target.files[0])
					}
					className="mb-4"
				/>
				<DialogFooter>
					<Button variant="ghost" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleCreate}
						disabled={
							!createName ||
							!createEntityModel ||
							!createTemplateType ||
							!createFile ||
							upsertTemplate.isPending
						}
						isLoading={upsertTemplate.isPending}
					>
						Créer
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default CreateTemplateDialog;
